import Modal from '@/components/ui/modal/modal';
import { Text } from '@/components/ui/text';
import { createStyles } from '@/hooks';
import { theme } from '@/theme/theme';
import React, { useCallback, useSyncExternalStore } from 'react';
import {
  Platform,
  Pressable,
  StyleSheet,
  View,
  type AlertButton,
  type ColorValue,
} from 'react-native';

export type WebAlertPayload = {
  title: string;
  message: string;
  buttons: AlertButton[];
  cancelable?: boolean;
};

let current: WebAlertPayload | null = null;
const pendingQueue: WebAlertPayload[] = [];

const listeners = new Set<() => void>();

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot() {
  return current;
}

function notify() {
  listeners.forEach(l => l());
}

function enqueue(payload: WebAlertPayload) {
  if (!current) {
    current = payload;
  } else {
    pendingQueue.push(payload);
  }
  notify();
}

function resolveCurrent() {
  current = pendingQueue.shift() ?? null;
  notify();
}

/** Imperative API used by `alert.tsx` on web only. */
export function enqueueWebAlert(payload: WebAlertPayload) {
  enqueue(payload);
}

function buttonLabelColor(btn: AlertButton): ColorValue {
  if (btn.style === 'destructive') {
    return theme.colors.Red;
  }
  return theme.colors['Accents/Blue'];
}

export function WebAlertHost() {
  const alert = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const styles = useStyles();

  const handleBackdrop = useCallback(() => {
    if (!alert?.cancelable) return;
    const cancelBtn = alert.buttons.find(b => b.style === 'cancel');
    if (cancelBtn) {
      cancelBtn.onPress?.();
    } else if (alert.buttons.length === 1) {
      alert.buttons[0]?.onPress?.();
    }
    resolveCurrent();
  }, [alert]);

  const onButtonPress = useCallback((btn: AlertButton) => {
    btn.onPress?.();
    resolveCurrent();
  }, []);

  if (Platform.OS !== 'web' || !alert) {
    return null;
  }

  const hasTitle = alert.title.trim().length > 0;
  const hasMessage = alert.message.trim().length > 0;
  const buttons = alert.buttons;

  return (
    <Modal
      visible
      onBackdropPress={alert.cancelable ? handleBackdrop : undefined}
      onBackButtonPress={alert.cancelable ? handleBackdrop : undefined}
      contentContainerStyle={styles.modalRoot}>
      <View style={styles.sheet}>
        {(hasTitle || hasMessage) && (
          <View style={styles.messageBlock}>
            {hasTitle ? (
              <Text variant="Font Inter/Body/Bold 16px" style={styles.title}>
                {alert.title}
              </Text>
            ) : null}
            {hasMessage ? (
              <Text variant="Font Inter/Subtitle/Regular 14px" style={styles.message}>
                {alert.message}
              </Text>
            ) : null}
          </View>
        )}
        <AlertButtonRows
          buttons={buttons}
          onButtonPress={onButtonPress}
          styles={styles}
          showBodyDivider={hasTitle || hasMessage}
        />
      </View>
    </Modal>
  );
}

type RowStyles = ReturnType<typeof useStyles>;

function AlertButtonRows({
  buttons,
  onButtonPress,
  styles,
  showBodyDivider,
}: {
  buttons: AlertButton[];
  onButtonPress: (b: AlertButton) => void;
  styles: RowStyles;
  showBodyDivider: boolean;
}) {
  const n = buttons.length;
  if (n === 0) return null;

  if (n === 1) {
    const btn = buttons[0];
    return (
      <Pressable
        accessibilityRole="button"
        style={({ pressed }) => [
          styles.stackButton,
          showBodyDivider && styles.stackButtonBelowBody,
          pressed && styles.buttonPressed,
        ]}
        onPress={() => onButtonPress(btn)}>
        <Text
          variant="Font Inter/Body/Medium 16px"
          style={[styles.buttonLabel, { color: buttonLabelColor(btn) }]}>
          {btn.text ?? ''}
        </Text>
      </Pressable>
    );
  }

  if (n === 2) {
    const [left, right] = buttons;
    return (
      <View style={styles.twoCol}>
        <Pressable
          accessibilityRole="button"
          style={({ pressed }) => [styles.halfCell, pressed && styles.buttonPressed]}
          onPress={() => onButtonPress(left)}>
          <Text
            variant="Font Inter/Body/Medium 16px"
            style={[styles.buttonLabel, { color: buttonLabelColor(left) }]}>
            {left.text ?? ''}
          </Text>
        </Pressable>
        <View style={styles.sepV} />
        <Pressable
          accessibilityRole="button"
          style={({ pressed }) => [styles.halfCell, pressed && styles.buttonPressed]}
          onPress={() => onButtonPress(right)}>
          <Text
            variant="Font Inter/Body/Medium 16px"
            style={[styles.buttonLabel, { color: buttonLabelColor(right) }]}>
            {right.text ?? ''}
          </Text>
        </Pressable>
      </View>
    );
  }

  return (
    <View style={styles.stack}>
      {buttons.map((btn, i) => (
        <React.Fragment key={`${btn.text ?? ''}-${i}`}>
          {i > 0 ? <View style={styles.sepH} /> : null}
          <Pressable
            accessibilityRole="button"
            style={({ pressed }) => [styles.stackButton, pressed && styles.buttonPressed]}
            onPress={() => onButtonPress(btn)}>
            <Text
              variant="Font Inter/Body/Medium 16px"
              style={[styles.buttonLabel, { color: buttonLabelColor(btn) }]}>
              {btn.text ?? ''}
            </Text>
          </Pressable>
        </React.Fragment>
      ))}
    </View>
  );
}

const useStyles = createStyles(({ scale, deviceWidth }) => ({
  modalRoot: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: scale(24),
  },
  sheet: {
    width: Math.min(deviceWidth - scale(48), scale(270)),
    borderRadius: scale(14),
    backgroundColor: theme.colors['Backgrounds/Primary'],
    overflow: 'hidden',
    ...(Platform.OS === 'web'
      ? ({
          boxShadow: '0 12px 28px rgba(0,0,0,0.28)',
        } as object)
      : {}),
  },
  messageBlock: {
    paddingHorizontal: scale(16),
    paddingTop: scale(20),
    paddingBottom: scale(16),
    alignItems: 'center',
    gap: scale(8),
  },
  title: {
    textAlign: 'center',
    color: theme.colors['Labels/Primary'],
  },
  message: {
    textAlign: 'center',
    color: theme.colors['Labels/Secondary'],
  },
  twoCol: {
    flexDirection: 'row',
    alignItems: 'stretch',
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: theme.colors['Separators/Vibrant'],
    minHeight: scale(44),
  },
  halfCell: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: scale(12),
    paddingHorizontal: scale(8),
  },
  sepV: {
    width: StyleSheet.hairlineWidth,
    backgroundColor: theme.colors['Separators/Vibrant'],
  },
  stack: {
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: theme.colors['Separators/Vibrant'],
  },
  sepH: {
    height: StyleSheet.hairlineWidth,
    backgroundColor: theme.colors['Separators/Vibrant'],
  },
  stackButton: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: scale(14),
    paddingHorizontal: scale(12),
    minHeight: scale(44),
  },
  stackButtonBelowBody: {
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: theme.colors['Separators/Vibrant'],
  },
  buttonPressed: {
    backgroundColor: theme.colors['Fills/Tertiary'],
  },
  buttonLabel: {
    textAlign: 'center',
  },
}));
